// CRUD de bodegas. Regla: no eliminar una bodega con existencias.
import { insforge } from '../insforge';
import { registrarMovimiento } from './movimientos';
import type { Bodega } from '../../types';

export async function listarBodegas(): Promise<Bodega[]> {
  const { data, error } = await insforge.database.from('bodegas').select('*').order('nombre');
  if (error) throw new Error(error.message);
  return (data ?? []) as Bodega[];
}

// Id de la bodega principal (o la primera si no hay ninguna marcada). Se usa
// como valor por defecto en los modales de fabricar/deshacer/mover/trasladar.
export function idBodegaPrincipal(bodegas: Bodega[]): string {
  return (bodegas.find((b) => b.principal) ?? bodegas[0])?.id ?? '';
}

export interface EntradaBodega {
  nombre: string;
  descripcion?: string;
  estado?: Bodega['estado'];
  principal?: boolean;
}

export async function crearBodega(input: EntradaBodega): Promise<Bodega> {
  const { data, error } = await insforge.database
    .from('bodegas')
    .insert([{ nombre: input.nombre, descripcion: input.descripcion ?? '', estado: input.estado ?? 'Activo', principal: input.principal ?? false }])
    .select();
  if (error) throw new Error(error.message);
  const bodega = (data as Bodega[])[0];
  await registrarMovimiento({
    tipo: 'Bodega creada',
    descripcion: 'Se creó la bodega ' + bodega.nombre,
    bodega_destino: bodega.id,
  });
  return bodega;
}

export async function editarBodega(id: string, input: EntradaBodega): Promise<Bodega> {
  const { data, error } = await insforge.database
    .from('bodegas')
    .update({ nombre: input.nombre, descripcion: input.descripcion ?? '', estado: input.estado ?? 'Activo', principal: input.principal ?? false })
    .eq('id', id)
    .select();
  if (error) throw new Error(error.message);
  const bodega = (data as Bodega[])[0];
  await registrarMovimiento({
    tipo: 'Bodega editada',
    descripcion: 'Se editó la bodega ' + bodega.nombre,
    bodega_origen: id,
  });
  return bodega;
}

export async function eliminarBodega(id: string): Promise<void> {
  // Regla de negocio: no eliminar con existencias (stock > 0 en cualquier ítem).
  const { data: conStock, error: e1 } = await insforge.database
    .from('inventario')
    .select('id')
    .eq('bodega_id', id)
    .gt('stock', 0)
    .limit(1);
  if (e1) throw new Error(e1.message);
  if (conStock && conStock.length > 0) {
    throw new Error('No se puede eliminar una bodega con existencias. Traslada o descuenta el stock primero.');
  }

  const { data: bodegas } = await insforge.database.from('bodegas').select('nombre').eq('id', id).limit(1);
  const nombre = (bodegas as { nombre: string }[] | null)?.[0]?.nombre ?? id;

  // El FK inventario.bodega_id ON DELETE CASCADE limpia las filas de stock 0.
  const { error } = await insforge.database.from('bodegas').delete().eq('id', id);
  if (error) throw new Error(error.message);
  await registrarMovimiento({ tipo: 'Bodega eliminada', descripcion: 'Se eliminó la bodega ' + nombre });
}
